/**
 * Bootstrap for visitors landing on `/` of any host.
 *
 * The product container serves multiple hosts:
 *
 *   - the canonical admin/product hosts (localhost, app.tafelfein.de,
 *     speisekarte.gaststaette-schellen.de) — `/` redirects to /admin/.
 *   - a tenant's CUSTOM host (e.g. speisekarte.gaststaette-schellen.de) where
 *     a diner has scanned the QR code — `/` should land on the menu.
 *
 * To resolve the second case we ask the backend
 * (`/api/public/outlets/by-host`) which outlet owns the current hostname
 * and route to /o/{slug}/. If the host isn't bound to anything we fall
 * back to /admin so the operator can still log in via a custom domain
 * during setup.
 *
 * Hook discipline: useQuery is called unconditionally to satisfy
 * rules-of-hooks. The known-host shortcut is `enabled: false` instead
 * of an early return.
 */

import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

const KNOWN_APP_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  'app.tafelfein.de',
  'speisekarte.gaststaette-schellen.de',
]);

function isKnownAppHost(hostname: string): boolean {
  if (KNOWN_APP_HOSTS.has(hostname)) return true;
  // Treat `*.tafelfein.de` (any subdomain) as known so future fallback
  // hosts like `schellen.tafelfein.de` don't trigger the lookup.
  return hostname.endsWith('.tafelfein.de');
}

interface ByHostResponse {
  slug: string;
  name: string;
  /** Which surface this host serves (TAF-6). 'admin' → /admin (the
   *  per-tenant admin subdomain); 'guest' → the QR menu. */
  kind: 'admin' | 'guest';
}

export function CustomHostBootstrap(): JSX.Element {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const knownHost = isKnownAppHost(hostname);

  const query = useQuery<ByHostResponse | null>({
    queryKey: ['outlet-by-host', hostname],
    enabled: !knownHost,
    queryFn: async () => {
      const res = await fetch(
        `/api/public/outlets/by-host?host=${encodeURIComponent(hostname)}`
      );
      if (!res.ok) return null;
      return (await res.json()) as ByHostResponse;
    },
    retry: false,
    staleTime: 60_000,
  });

  if (knownHost) {
    return <Navigate to="/admin" replace />;
  }

  if (query.isLoading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted">
        Wird geladen…
      </div>
    );
  }
  if (!query.data) {
    // Unknown host with no outlet binding → operator fallback.
    return <Navigate to="/admin" replace />;
  }
  // Authoritative: the backend says whether this host is the verified
  // admin subdomain or the guest QR host — no hostname guessing.
  if (query.data.kind === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to={`/o/${query.data.slug}/`} replace />;
}
